import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/news";
import { getCategoryByKey, relativeTime, formatViews } from "@/utils/news";

interface NewsCardProps {
  item: NewsItem;
  variant?: "grid" | "list" | "compact";
  showCategory?: boolean;
}

export default function NewsCard({ item, variant = "grid", showCategory = true }: NewsCardProps) {
  const category = getCategoryByKey(item.category);
  const to = `/detail/${item.id}`;

  if (variant === "compact") {
    return (
      <Link
        to={to}
        className="group flex gap-3 items-center py-3 cursor-pointer"
      >
        <div className="w-24 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 51吃瓜官网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {item.title}
          </h4>
          <span className="text-xs text-foreground-500 mt-1 block">
            {relativeTime(item.publishedAt)}
          </span>
        </div>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link
        to={to}
        className="group flex flex-col sm:flex-row gap-4 bg-background-50 border border-background-200 rounded-lg p-3 hover:border-primary-200 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-56 h-40 sm:h-36 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 51吃瓜官网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          {showCategory && category && (
            <span className="inline-flex w-fit items-center gap-1 text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full mb-2">
              <i className={category.icon}></i>
              {category.name}
            </span>
          )}
          <h3 className="text-base font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {item.title}
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mt-1.5 hidden sm:block">
            {item.excerpt}
          </p>
          <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {relativeTime(item.publishedAt)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(item.views)}
            </span>
            <span className="hidden md:flex items-center gap-1">
              <i className="ri-price-tag-3-line"></i>
              {item.tags[0]}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-200 transition-colors cursor-pointer"
    >
      <div className="w-full h-44 bg-background-200 overflow-hidden">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title} - 51吃瓜官网`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="flex-1 flex flex-col p-4">
        {showCategory && category && (
          <span className="inline-flex w-fit items-center gap-1 text-xs text-secondary-700 bg-secondary-100 px-2 py-0.5 rounded-full mb-2">
            <i className={category.icon}></i>
            {category.name}
          </span>
        )}
        <h3 className="text-base font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {item.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mt-2">
          {item.excerpt}
        </p>
        <div className="mt-auto pt-3 flex items-center gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {relativeTime(item.publishedAt)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(item.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}